/**
 * Small, request/app-scoped cache for public country and currency metadata only.
 * Never use this for cart, customer, balance or payment data. A Nuxt SSR app is
 * request-local; WeakMap ownership prevents sharing mutable results across users.
 */
type CacheEntry = {
  pending?: Promise<unknown>;
  value?: unknown;
  expiresAt: number;
};

const appCaches = new WeakMap<object, Map<string, CacheEntry>>();
const PUBLIC_METADATA_TTL = 5 * 60 * 1000;
const MAX_SETTLED_ENTRIES = 200;

const clone = <T>(value: T): T => (value == null ? value : JSON.parse(JSON.stringify(value)) as T);

const sorted = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sorted);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sorted(value[key])]));
  }
  return value;
};

/** Read the scope at request start and again before publishing an async result. */
export const publicReadScope = (app: any, currentSite?: string): string => {
  const state = app?.$vsf?.$magento?.config?.state;
  return JSON.stringify([
    currentSite ?? '',
    state?.getStore?.() ?? '',
    state?.getLocale?.() ?? '',
    app?.i18n?.locale ?? '',
    state?.getCurrency?.() ?? '',
  ]);
};

export const publicReadKey = (kind: string, scope: string, params: unknown = null): string => (
  JSON.stringify([kind, scope, sorted(params)])
);

/** Explicit read overrides must not publish metadata into the active Store. */
export const publicReadHeadersMatch = (app: any, headers?: Record<string, string>): boolean => {
  const state = app?.$vsf?.$magento?.config?.state;
  const localeValues = [state?.getLocale?.(), app?.i18n?.locale, app?.i18n?.localeProperties?.iso]
    .filter(Boolean).map((value: string) => value.toLowerCase());
  return Object.entries(headers ?? {}).every(([name, value]) => {
    const header = name.toLowerCase();
    if (header === 'store') return value === state?.getStore?.();
    if (header === 'content-currency') return value.toUpperCase() === state?.getCurrency?.()?.toUpperCase();
    if (header === 'accept-language' || header === 'content-language') return localeValues.includes(value.toLowerCase());
    return true;
  });
};

export const readPublicMetadata = async <T>(
  app: object,
  key: string,
  load: () => Promise<T>,
): Promise<T> => {
  let cache = appCaches.get(app);
  if (!cache) {
    cache = new Map();
    appCaches.set(app, cache);
  }
  const now = Date.now();
  const existing = cache.get(key);
  if (existing?.pending !== undefined) return clone(await existing.pending as T);
  if (existing && existing.expiresAt > now) return clone(existing.value as T);

  // Evict expired settled values, but never interrupt another in-flight read.
  cache.forEach((cachedEntry, cachedKey) => {
    if (cachedEntry.pending === undefined && cachedEntry.expiresAt <= now) cache.delete(cachedKey);
  });
  if (cache.size >= MAX_SETTLED_ENTRIES) {
    const oldestSettled = [...cache].find(([, cachedEntry]) => cachedEntry.pending === undefined);
    if (oldestSettled) cache.delete(oldestSettled[0]);
  }

  const entry: CacheEntry = { expiresAt: 0 };
  cache.set(key, entry);
  try {
    // Start immediately while the API's store/locale context still matches key.
    entry.pending = Promise.resolve(load()).then((value) => {
      entry.value = clone(value);
      entry.expiresAt = Date.now() + PUBLIC_METADATA_TTL;
      entry.pending = undefined;
      return entry.value;
    });
    return clone(await entry.pending as T);
  } catch (error) {
    // Errors and invalid responses are never cached; a later call can retry.
    if (cache.get(key) === entry) cache.delete(key);
    throw error;
  }
};
